import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Loader, X, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AutocompleteInput = ({
  value,
  onChange,
  onSelect,
  placeholder = "Enter Part Number or Description",
  className = "",
  disabled = false,
  debounceMs = 300,
  maxResults = 25
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasSearched, setHasSearched] = useState(false);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Use Elasticsearch autocomplete API (same as Advanced Search)
      const response = await fetch(`https://api.mhsonline.au/api/autocomplete/?q=${encodeURIComponent(query)}&limit=${maxResults}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const suggestions = data.suggestions || [];
      
      // Filter out exact matches if user typed the exact part number
      const filteredSuggestions = suggestions.filter(item => 
        item.part_number.toLowerCase() !== query.toLowerCase().trim()
      );

      setSuggestions(filteredSuggestions);
      setIsOpen(filteredSuggestions.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Autocomplete search error:', error);
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }, [maxResults]);

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounce
    debounceRef.current = setTimeout(() => {
      debouncedSearch(newValue);
    }, debounceMs);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    onChange(suggestion.part_number);
    setIsOpen(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    setHasSearched(false);
    
    if (onSelect) {
      onSelect(suggestion);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && suggestions.length === 0 && value.trim().length >= 2) {
        // Trigger search if dropdown is closed but we have input
        debouncedSearch(value);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Handle input focus
  const handleFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    } else if (value.trim().length >= 2) {
      debouncedSearch(value);
    }
  };

  const clearInput = () => {
    onChange('');
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    setHasSearched(false);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-4 py-2 md:py-3 bg-white border-2 border-brand-dark rounded-lg md:rounded-l-lg focus:outline-none focus:ring-2 focus:ring-brand-dark focus:border-transparent text-brand-dark placeholder-gray-500 pr-20"
          autoComplete="off"
        />
        
        {/* Loading spinner */}
        {loading && (
          <div className="absolute right-12 top-1/2 transform -translate-y-1/2">
            <Loader className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        )}

        {/* Clear button */}
        {value && !loading && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Dropdown indicator */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          {isOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </div>

      {/* Suggestions dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto mt-1"
          >
            {suggestions.length > 0 ? (
              <ul className="py-1">
                {suggestions.map((suggestion, index) => (
                  <li key={`${suggestion.part_number}-${index}`}>
                    <button
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors ${
                        index === selectedIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="font-medium text-gray-900 truncate">
                            {suggestion.part_number}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {suggestion.description}
                          </div>
                          {suggestion.designation && (
                            <div className="text-xs text-gray-400 italic truncate">
                              {suggestion.designation}
                            </div>
                          )}
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          {suggestion.picture_mini ? (
                            <img
                              src={suggestion.picture_mini}
                              alt={suggestion.part_number}
                              className="w-8 h-8 object-cover border border-gray-200 rounded"
                              onError={(e) => {
                                // Fallback to Package icon if image fails to load
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-8 h-8 bg-gray-100 border border-gray-200 rounded flex items-center justify-center ${suggestion.picture_mini ? 'hidden' : ''}`}
                          >
                            <Package className="w-4 h-4 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : hasSearched && !loading ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No matching parts found
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutocompleteInput;
